import { logger } from '../../utils/logger';
// src/services/secureStorage.ts
class SecureStorage {
  private readonly ACCESS_TOKEN_KEY = "poh.access";
  private readonly REFRESH_TOKEN_KEY = "poh.refresh";
  private readonly USER_DATA_KEY = "poh.user";

  saveTokens(accessToken: string, refreshToken: string) {
    try {
      // Encrypt tokens before storage
      const encryptedAccess = this.encrypt(accessToken);
      const encryptedRefresh = this.encrypt(refreshToken);

      localStorage.setItem(this.ACCESS_TOKEN_KEY, encryptedAccess);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, encryptedRefresh);

      // Set expiration metadata
      const expirationTime = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days
      localStorage.setItem("poh.refresh.expires", expirationTime.toString());
      
      // Debug logging removed for cleaner console
    } catch (error) {
      console.error("Token storage error:", error);
    }
  }

  getAccessToken(): string | null {
    try {
      const encrypted = localStorage.getItem(this.ACCESS_TOKEN_KEY);
      return encrypted ? this.decrypt(encrypted) : null;
    } catch (error) {
      console.error("Token retrieval error:", error);
      return null;
    }
  }

  getRefreshToken(): string | null {
    try {
      const encrypted = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      const expiration = localStorage.getItem("poh.refresh.expires");

      if (!encrypted || !expiration) return null;

      // Check if refresh token is expired
      if (Date.now() > parseInt(expiration)) {
        logger.log("Refresh token expired, clearing tokens");
        this.clearTokens();
        return null;
      }

      return this.decrypt(encrypted);
    } catch (error) {
      console.error("Refresh token retrieval error:", error);
      return null;
    }
  }

  saveUserData(user: any) {
    try {
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(user));
      // Debug logging removed for cleaner console
    } catch (error) {
      console.error("User data storage error:", error);
    }
  }

  getUserData(): any | null {
    try {
      const data = localStorage.getItem(this.USER_DATA_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("User data retrieval error:", error);
      return null;
    }
  }

  clearTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
    localStorage.removeItem("poh.refresh.expires");
      // Debug logging removed for cleaner console
  }

  private encrypt(text: string): string {
    // Simple encryption - replace with proper encryption in production
    // For now, using base64 encoding as a simple obfuscation
    return btoa(text);
  }

  private decrypt(encryptedText: string): string {
    // Simple decryption - replace with proper decryption in production
    return atob(encryptedText);
  }
}

export const secureStorage = new SecureStorage();
